import { Button, Drawer, Loading } from "@components";
import GestorLogo from "shared/containers/GestorLogo";
import { EnvironmentStorage } from "shared/tools/EnvironmentStorage";
import styles from "styles/onboarding.module.scss";
import { useNavigate } from "react-router";
import { OnboardingAPI } from "providers/OnboardingAPI";
import { useEffect, useState } from "react";

import classNames from "classnames/bind";
import { If } from "shared/components/control-statements";
import { EnvironmentsAPI } from "providers/EnvironmentsAPI";
import { UserStorage } from "shared/tools/UserStorage";
import { Environment } from "shared/core/Environment";
import { AccessManager } from "shared/tools/AccessManager";
const css = classNames.bind(styles);

export default function Onboarding() {
  const environment = EnvironmentStorage.getEnvironment();
  const [brands, setBrands] = useState([]);
  const [units, setUnits] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openSend, setOpenSend] = useState({});
  const [title, setTitle] = useState("");
  const [sending, setSending] = useState(false);
  const [openSent, setOpenSent] = useState(false);

  useEffect(() => {
    setConfigs([
      {
        name: "Marcas",
        icon: "fal fa-store",
        description: "Clique aqui para configurar suas marcas.",
        link: "/implantacao/marcas",
        progress: getCompletedBrandsPercentage(),
      },
      {
        name: "Unidades",
        icon: "fal fa-store",
        description: "Clique aqui para configurar suas unidades.",
        link: "/implantacao/unidades",
        progress: getCompletedUnitsPercentage(),
      },
      {
        name: "Mapeamento",
        icon: "fal fa-list",
        description: "Configure o mapeamento do projeto no botão abaixo.",
        link: "/implantacao/mapeamento",
        progress: getCompletedMappingsPercentage(),
      },
    ]);
  }, [brands, units]);

  const navigate = useNavigate();

  const getBrands = async () => {
    const response = await OnboardingAPI.getBrands();
    setBrands(response);
  };

  const getUnits = async () => {
    const response = await OnboardingAPI.getUnits();
    setUnits(response);
  };

  const getMappings = async () => {
    const response = (await OnboardingAPI.getMappings()) || [];
    setMappings(response || []);
  };

  useEffect(() => {
    const getHolding = async () => {
      const environmentId = await EnvironmentStorage.getEnvironmentHoldingId();

      const holdingResponse = await EnvironmentsAPI.getById(environmentId);
      const holding = holdingResponse.getData({});
      if (holding.name) setTitle(holding.name || environment?.brand?.name);
    };

    Promise.all([getBrands(), getUnits(), getMappings(), getHolding()]).then(
      () => {
        setLoading(false);
      }
    );
  }, []);

  const getCompletedBrandsPercentage = () => {
    const validLinkRegex = /^(https?:\/\/)?([\w-]+\.)+(com|com\.br)(\/.*)?$/i;

    if (!Array.isArray(brands) || brands.length === 0) return 0;

    const requiredFields = ["logo", "name", "description"];
    const totalFields = brands.length * requiredFields.length;

    let filledCount = 0;

    for (const item of brands) {
      for (const field of requiredFields) {
        const value = item[field];
        if (!value) continue;

        if (field === "url") {
          if (validLinkRegex.test(value)) filledCount++;
        } else {
          filledCount++;
        }
      }
    }

    const percentage = (filledCount / totalFields) * 100;
    return Number(percentage.toFixed(0));
  };

  function getCompletedUnitsPercentage() {
    if (!Array.isArray(units) || units.length === 0) return 0;

    const requiredFields = [
      "name",
      "cnpj",
      "address",
    ];
    const totalFields = units.length * requiredFields.length;

    let filledCount = 0;

    for (const item of units) {
      for (const field of requiredFields) {
        const value = item[field];
        if (!value) continue;

        filledCount++;
      }
    }

    const percentage = (filledCount / totalFields) * 100;
    return Number(percentage.toFixed(0));
  }

  function getCompletedMappingsPercentage() {
    if (!Array.isArray(mappings) || mappings.length === 0) return 0;

    const requiredFields = ["pdv", "logistic"];
    const totalFields = mappings.length * requiredFields.length;

    let filledCount = 0;

    for (const item of mappings) {
      for (const field of requiredFields) {
        const value = item[field];
        if (!value) continue;

        filledCount++;
      }
    }

    const percentage = (filledCount / totalFields) * 100;
    return Number(percentage.toFixed(0));
  }

  const save = async () => {
    setSending(true);
    const response = await OnboardingAPI.putStatus(
      `/status/${openSend.status === "APPROVING" ? "approve" : "publish"}`
    );

    if (response.getErrors([]).length > 0) {
      alert({ text: response.getErrors()[0], buttonText: "Ok" });
      setSending(false);
      return false;
    }

    if (openSend.status === "FINISHED") navigate("/");
    else setOpenSent(true);
    setOpenSend({ open: false, status: "" });
    setSending(false);
  };

  return (
    <div className={styles.onboarding}>
      <div className={styles.headerContainer}>
        <div className={styles.content}>
          <div className="d-flex align-center">
            <div className={styles.logo}>
              <GestorLogo />
            </div>
            <p className={styles.title}>{title || ""}</p>
          </div>
          <div className="d-flex align-center gap-10">
            <If cond={UserStorage.isGodMode}>
              <Button
                onClick={() => setOpenSend({ open: true, status: "FINISHED" })}
                className="gap-10"
              >
                <i className="fal fa-lock" />
                <span>Liberar ambiente</span>
              </Button>
            </If>

            <Button
              design="primary"
              disabled={configs.some((c) => c.progress !== 100)}
              onClick={() => setOpenSend({ open: true, status: "APPROVING" })}
            >
              Enviar para aprovação
            </Button>
            <Button
              design="primary-outline"
              onClick={() => {
                const configurations = EnvironmentStorage.getConfigurations();

                UserStorage.setAccessToken(null);
                EnvironmentStorage.setEnvironment(null);
                EnvironmentStorage.setConfigurations(null);
                Environment.uninstall();
                localStorage.removeItem("refreshOrdersAuto");
                localStorage.removeItem(
                  `${configurations?.parent_alias}-refreshOrdersAuto`
                );
                AccessManager.setPermissions([]);
                navigate("/entrar");
              }}
            >
              <i className="far fa-right-from-bracket" />
            </Button>
          </div>
        </div>
      </div>

      <div className={styles.configsContainer}>
        <If cond={loading}>
          <Loading onboarding />
        </If>

        <div className={styles.configs}>
          {configs.map((c) => {
            return (
              <div
                className={css({ config: true, completed: c.progress === 100 })}
              >
                <div className={styles.header}>
                  <div className={styles.picture}>
                    <i className={c.icon} />
                    <If cond={c.progress === 100}>
                      <div className={styles.completed}>
                        <svg
                          width="48"
                          height="48"
                          viewBox="0 0 48 48"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M0 24C0 10.7452 10.7452 0 24 0C37.2548 0 48 10.7452 48 24C48 37.2548 37.2548 48 24 48C10.7452 48 0 37.2548 0 24Z"
                            fill="#1d2421"
                          />
                          <path
                            d="M9 24C9 15.7157 15.7157 9 24 9C32.2843 9 39 15.7157 39 24C39 32.2843 32.2843 39 24 39C15.7157 39 9 32.2843 9 24Z"
                            fill="#51D9A3"
                            fillOpacity="0.15"
                          />
                          <path
                            d="M29.7284 19.5732C29.8904 19.7384 29.8904 20.0103 29.7284 20.1755L22.6233 27.4253C22.4613 27.5905 22.1949 27.5905 22.0329 27.4253L18.2714 23.5872C18.1094 23.4219 18.1094 23.1501 18.2714 22.9848C18.4333 22.8196 18.6998 22.8196 18.8617 22.9848L22.3281 26.5217L29.1381 19.5732C29.3 19.4079 29.5665 19.4079 29.7284 19.5732Z"
                            fill="#51D9A3"
                          />
                        </svg>
                      </div>
                    </If>

                    <If cond={c.progress !== 100}>
                      <div className={styles.completeness}>
                        <div className={styles.total}>
                          <span>{c.progress}%</span>
                        </div>
                      </div>
                    </If>
                  </div>
                </div>
                <div className={styles.content}>
                  <p className={styles.title}>{c.name}</p>
                  <p className={styles.description}>{c.description}</p>
                  <Button onClick={() => navigate(c.link)}>Configurar</Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Drawer
        visible={openSend.open}
        onClose={() =>
          setOpenSend({
            open: false,
            status: "",
          })
        }
        direction="center"
        showCloseButton={false}
      >
        <div className={styles.success}>
          <p className={styles.title}>Atenção</p>
          <p className={styles.subtitle}>
            Deseja prosseguir e enviar estes dados para aprovação? A Olga será
            avisada assim que o envio for concluído.
          </p>
          <Button
            design="primary"
            className="w-100 mb-15"
            onClick={save}
            loading={sending}
            disabled={sending}
          >
            Enviar
          </Button>
          <Button
            className="w-100"
            onClick={() =>
              setOpenSend({
                open: false,
                status: "",
              })
            }
            disabled={sending}
          >
            Cancelar
          </Button>
        </div>
      </Drawer>

      <Drawer
        visible={openSent}
        onClose={() => setOpenSent(false)}
        direction="center"
        showCloseButton={false}
      >
        <div className={styles.success}>
          <div className="d-flex align-center justify-center mb-20">
            <svg
              width="30"
              height="30"
              viewBox="0 0 30 30"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0 15C0 6.71573 6.71573 0 15 0C23.2843 0 30 6.71573 30 15C30 23.2843 23.2843 30 15 30C6.71573 30 0 23.2843 0 15Z"
                fill="#51D9A3"
                fill-opacity="0.15"
              />
              <path
                d="M20.7289 10.5732C20.8909 10.7384 20.8909 11.0103 20.7289 11.1755L13.6238 18.4253C13.4618 18.5905 13.1954 18.5905 13.0334 18.4253L9.27186 14.5872C9.1099 14.4219 9.1099 14.1501 9.27186 13.9848C9.43381 13.8196 9.70026 13.8196 9.86221 13.9848L13.3286 17.5217L20.1386 10.5732C20.3005 10.4079 20.567 10.4079 20.7289 10.5732Z"
                fill="#51D9A3"
              />
            </svg>
          </div>

          <p className={styles.title}>Sucesso</p>
          <p className={styles.subtitle}>Dados enviados para a aprovação.</p>
          <Button
            className="w-100"
            onClick={() => setOpenSent(false)}
            disabled={sending}
          >
            Fechar
          </Button>
        </div>
      </Drawer>
    </div>
  );
}
